import React, { useState } from 'react';
import classNames from 'classnames';
import { DataRoomMember } from 'router/subrouters/DataRoom/models/DataRoomMember';
import { Button } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import PauseIcon from 'icons/Pause.icon';
import useUndoActions from 'hooks/useUndoActions';
import UndoButton from 'components/UndoButton';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import useCallbackAsync from 'hooks/useCallbackAsync';
import { getUserFullNameOrEmail } from 'utils';
import {
  getFormattedDate,
  DATE_FORMAT_DEFAULT,
  TIME_FORMAT_12H,
  isDateFromLastDays,
} from 'dateUtils';

import './UserWithAccess.styles.scss';

type UserWithAccessProps = {
  className?: string;
  nodeId: string;
  member: Partial<DataRoomMember>;
  onRevokeAccessSuccess?: () => void;
};

const UserWithAccess: React.FC<UserWithAccessProps> = (props) => {
  const { className, nodeId, member, onRevokeAccessSuccess } = props;

  const {
    _id: memberId,
    canRevoke,
    lastAccessNodeAt,
    hasAccessToOwnFiles,
  } = member || {};

  const [isRevokedAccess, setIsRevokedAccess] = useState(false);

  const classes = classNames('user-with-access', className);

  const canOnlyAccessOwnFiles = hasAccessToOwnFiles && isRevokedAccess;

  const { t } = useTranslation();

  const { isUndoAvailable, setUndoTimeout, clearUndoTimeout } =
    useUndoActions();

  const [onRevokeAccess, isRevokingAccess] = useCallbackAsync(async () => {
    await dataRoomApi.members.revokeAccessForNode(memberId, nodeId);

    setUndoTimeout(() => {
      onRevokeAccessSuccess();
      setIsRevokedAccess(true);
    });
  });

  const [onUndo, isUndoing] = useCallbackAsync(async () => {
    clearUndoTimeout();

    await dataRoomApi.members.undoRevokeAccessForNode(memberId, nodeId);
  });

  return (
    <div className={classes}>
      <div>
        <p className={classNames({ 'not-viewed': !lastAccessNodeAt })}>
          {getUserFullNameOrEmail(member) || t('unknownUser')}
        </p>
        {lastAccessNodeAt && (
          <p className="last-access-node-at">
            {t('viewedOn', {
              date: getFormattedDate(
                new Date(lastAccessNodeAt),
                isDateFromLastDays(new Date(lastAccessNodeAt), 1)
                  ? TIME_FORMAT_12H
                  : DATE_FORMAT_DEFAULT,
              ),
            })}
          </p>
        )}

        {canOnlyAccessOwnFiles && (
          <p className="own-files-only">
            {t('revokedAccessForFilesNotUploadedUser')}
          </p>
        )}
      </div>

      {canRevoke &&
        (isUndoAvailable ? (
          <UndoButton disabled={isUndoing} onUndo={onUndo} />
        ) : (
          <Button
            variant="link"
            icon={PauseIcon}
            disabled={isRevokingAccess}
            onClick={onRevokeAccess}
          >
            {t('revoke')}
          </Button>
        ))}
    </div>
  );
};

export default UserWithAccess;
